import React, { memo } from 'react';
import { ColumnDef } from 'types/admin';
import StatusBadge from './StatusBadge';
import IconButton from './IconButton';

interface AdminTableProps<T = any> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onEdit?: (item: T, index: number) => void;
  onDelete?: (item: T, index: number) => void;
  renderActions?: (item: T, index: number) => React.ReactNode;
  showSerialNumber?: boolean;
  className?: string;
}

/**
 * Reusable Admin Table Component
 * Consistent table structure with built-in loading, empty states, and actions
 * 
 * @param columns - Column definitions
 * @param data - Array of data items
 * @param loading - Show loading state
 * @param emptyMessage - Message when no data
 * @param onEdit - Edit action handler
 * @param onDelete - Delete action handler
 * @param renderActions - Custom actions renderer (overrides default edit/delete)
 * @param showSerialNumber - Show Sl.No. column (default: true)
 * @param className - Additional CSS classes
 */
function AdminTableInner<T = any>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data found',
  onEdit,
  onDelete,
  renderActions,
  showSerialNumber = true,
  className = '',
}: AdminTableProps<T>) {
  // Show loading state
  if (loading) {
    return (
      <div className={`relative overflow-x-auto sm:rounded-lg ${className}`}>
        <p className="text-center py-8 text-gray-500">Loading...</p>
      </div>
    );
  }

  // Show empty state
  if (data.length === 0) {
    return (
      <div className={`relative overflow-x-auto sm:rounded-lg ${className}`}>
        <p className="text-center py-8 text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  // Determine if we need actions column
  const showActions = onEdit || onDelete || renderActions;

  return (
    <div className={`relative overflow-x-auto sm:rounded-lg ${className}`}>
      <table className="w-full text-sm text-left text-gray-500">
        <thead className="text-xs text-gray-700 p-4 bg-blue-50">
          <tr>
            {/* Serial Number Column */}
            {showSerialNumber && (
              <th scope="col" className="px-6 py-4 rounded-l-lg">
                <p className="text-lightgray-200">Sl.No.</p>
              </th>
            )}

            {/* Dynamic Columns */}
            {columns.map((column, idx) => (
              <th
                key={String(column.key)}
                scope="col"
                className={`px-6 py-4 ${column.className || ''}`}
              >
                <p className="text-lightgray-200">{column.header}</p>
              </th>
            ))}

            {/* Actions Column */}
            {showActions && (
              <th scope="col" className="px-6 py-4 rounded-r-lg">
                <p className="text-lightgray-200"></p>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {data.map((item, index) => (
            <tr
              key={(item as any).id || index}
              className="odd:bg-white even:bg-blue-50 border hover:bg-gray-100 transition-colors"
            >
              {/* Serial Number */}
              {showSerialNumber && (
                <td className="px-6 py-4">{index + 1}</td>
              )}

              {/* Dynamic Columns */}
              {columns.map((column) => (
                <td
                  key={String(column.key)}
                  className={`px-6 py-4 ${column.className || ''}`}
                >
                  {column.render ? (
                    column.render(item, index)
                  ) : (
                    <span>{String((item as any)[column.key] || '')}</span>
                  )}
                </td>
              ))}

              {/* Actions Column */}
              {showActions && (
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-3">
                    {renderActions ? (
                      renderActions(item, index)
                    ) : (
                      <>
                        {onEdit && (
                          <IconButton
                            variant="edit"
                            onClick={() => onEdit(item, index)}
                            title="Edit"
                            ariaLabel={`Edit item ${index + 1}`}
                          />
                        )}
                        {onDelete && (
                          <IconButton
                            variant="delete"
                            onClick={() => onDelete(item, index)}
                            title="Delete"
                            ariaLabel={`Delete item ${index + 1}`}
                          />
                        )}
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Use memo to prevent unnecessary re-renders
const AdminTable = memo(AdminTableInner) as typeof AdminTableInner;

export default AdminTable;

